"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import {
  Menu,
  X,
  ChevronDown,
  Search,
  Palette,
} from "lucide-react";

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setIsCategoriesOpen(false);
    }
  };

  const toggleCategories = () => {
    setIsCategoriesOpen(!isCategoriesOpen);
  };

  const toggleSearch = () => {
    setSearchOpen(!searchOpen);
  };

  const categories = [
    { name: "Adult Stress Relief", href: "/adult-coloring-books-relaxation" },
    { name: "Cute Kawaii", href: "/cute-kawaii" },
    { name: "Cozy Seasons", href: "/cozy-seasons" },
    { name: "Bold & Easy", href: "/bold-and-easy" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/90 backdrop-blur-md shadow-md py-2"
          : "bg-white/50 backdrop-blur-sm text-gray-800 py-4"
      }`}
    >
      <div className="px-4 mx-auto max-w-7xl">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="z-10 flex items-center space-x-2">
            <div className="flex items-center justify-center w-10 h-10 rounded-full shadow-lg bg-gradient-to-r from-pink-500 to-purple-600">
              <Palette className="w-6 h-6 text-white" />
            </div>
            <span
              className={`text-xl font-bold ${
                isScrolled ? "text-gray-800" : "text-gray-900"
              } transition-colors duration-300`}
            >
              ColoringBooksShop
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="items-center hidden space-x-1 lg:flex">
            <NavLink href="/" isScrolled={isScrolled}>
              Home
            </NavLink>

            {/* Categories Dropdown */}
            <div className="relative group">
              <button
                onClick={toggleCategories}
                className={`flex items-center px-4 py-2 rounded-full font-medium transition-all duration-300 ${
                  isScrolled
                    ? "text-gray-700 hover:text-purple-600"
                    : "text-gray-900 hover:text-gray-700"
                }`}
                aria-haspopup="true"
                aria-expanded={isCategoriesOpen}
              >
                Categories
                <ChevronDown
                  className={`w-4 h-4 ml-1 transition-transform duration-300 ${
                    isCategoriesOpen ? "rotate-180" : ""
                  } ${
                    isScrolled ? "text-gray-700" : "text-gray-900"
                  }`}
                />
              </button>

              <div className="absolute left-0 invisible w-64 mt-2 transition-all duration-300 origin-top-left transform opacity-0 group-hover:opacity-100 group-hover:visible">
                <div className="p-1 bg-white border border-gray-100 shadow-xl rounded-xl">
                  {categories.map((category) => (
                    <Link
                      key={category.name}
                      href={category.href}
                      className="block px-4 py-3 text-gray-700 transition-colors duration-300 rounded-lg hover:bg-gradient-to-r hover:from-pink-50 hover:to-purple-50"
                    >
                      {category.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <NavLink href="/free-coloring-pages" isScrolled={isScrolled}>
              Free Pages
            </NavLink>
            <NavLink href="/gallery" isScrolled={isScrolled}>
              Gallery
            </NavLink>
            <NavLink href="/contact" isScrolled={isScrolled}>
              Contact
            </NavLink>
            <NavLink href="/blog" isScrolled={isScrolled}>
              Blog
            </NavLink>
          </div>

          {/* Desktop Right Icons */}
          <div className="items-center hidden space-x-2 lg:flex">
          
            <Link
              href="/free-coloring-pages"
              className="px-5 py-2 ml-2 font-medium text-white transition-all duration-300 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 hover:shadow-2xl hover:scale-105"
            >
              Download Free Pages
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center space-x-2 lg:hidden">
            <button
              onClick={toggleMenu}
              className={`p-2 rounded-full transition-colors duration-300 ${
                isScrolled
                  ? "text-gray-700 hover:text-purple-600"
                  : "text-white hover:text-white/80"
              }`}
              aria-label="Toggle menu"
            >
              {isOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Search Bar - Conditional Render */}
        {searchOpen && (
          <div className="absolute left-0 right-0 p-4 bg-white border-t border-gray-100 shadow-lg rounded-b-xl animate-slideDown">
            <div className="relative max-w-3xl mx-auto">
              <input
                type="text"
                placeholder="Search for coloring books..."
                className="w-full px-4 py-3 pl-10 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <Search className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
              <button
                onClick={toggleSearch}
                className="absolute text-gray-400 right-3 top-3 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Navigation Menu */}
      {isOpen && (
        <div className="absolute left-0 right-0 bg-white border-t border-gray-100 shadow-lg lg:hidden top-full rounded-b-xl animate-slideDown">
          <div className="px-4 py-6 max-h-[80vh] overflow-y-auto">
            <ul className="space-y-1">
              <MobileNavLink href="/" onClick={() => setIsOpen(false)}>
                Home
              </MobileNavLink>

              <li>
                <button
                  onClick={toggleCategories}
                  className="flex items-center justify-between w-full px-4 py-3 text-gray-700 rounded-lg hover:bg-gradient-to-r hover:from-pink-50 hover:to-purple-50"
                >
                  <span>Categories</span>
                  <ChevronDown
                    className={`w-5 h-5 transition-transform duration-300 ${
                      isCategoriesOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {isCategoriesOpen && (
                  <ul className="mt-1 ml-4 space-y-1">
                    {categories.map((category) => (
                      <li key={category.name}>
                        <Link
                          href={category.href}
                          className="block px-4 py-3 text-gray-600 rounded-lg hover:bg-gradient-to-r hover:from-pink-50 hover:to-purple-50"
                          onClick={() => setIsOpen(false)}
                        >
                          {category.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>

              <MobileNavLink
                href="/free-coloring-pages"
                onClick={() => setIsOpen(false)}
              >
                Free Pages
              </MobileNavLink>
              <MobileNavLink href="/gallery" onClick={() => setIsOpen(false)}>
                Gallery
              </MobileNavLink>
              <MobileNavLink href="/contact" onClick={() => setIsOpen(false)}>
                Contact
              </MobileNavLink>
              <MobileNavLink href="/blog" onClick={() => setIsOpen(false)}>
                Blog
              </MobileNavLink>
            </ul>

            <div className="pt-6 mt-6 border-t border-gray-100">
              <Link
                href="/free-coloring-pages"
                className="block w-full px-4 py-3 font-medium text-center text-white transition-all duration-300 rounded-lg bg-gradient-to-r from-pink-500 to-purple-600 hover:shadow-lg"
                onClick={() => setIsOpen(false)}
              >
                Download Free Pages
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}

// Helper component for desktop nav links
function NavLink({
  href,
  children,
  isScrolled,
}: {
  href: string;
  children: React.ReactNode;
  isScrolled: boolean;
}) {
  return (
    <Link
      href={href}
      className={`px-4 py-2 rounded-full font-medium transition-all duration-300 ${
        isScrolled
          ? "text-gray-700 hover:text-purple-600"
          : "text-gray-900 hover:text-gray-700"
      }`}
    >
      {children}
    </Link>
  );
}

// Helper component for mobile nav links
function MobileNavLink({
  href,
  children,
  onClick,
}: {
  href: string;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <li>
      <Link
        href={href}
        className="block px-4 py-3 text-gray-900 rounded-lg hover:bg-gradient-to-r hover:from-pink-50 hover:to-purple-50"
        onClick={onClick}
      >
        {children}
      </Link>
    </li>
  );
}
